import { useTranslation } from 'react-i18next'

import styles from './StepVisualDeal.module.scss'

/**
 * ШАГ 06 — «Вы закрываете сделку», миниатюра 320×180 для блока «как это работает».
 *
 * ЭТОГО ЭКРАНА В ПРОДУКТЕ НЕТ И НЕ БУДЕТ, и здесь его больше, чем было в прошлой версии. Продукт
 * заканчивается ПЕРВЫМ СООБЩЕНИЕМ (`POST /api/runs/:id/draft`): ответов, переписки, встреч,
 * календаря, длительностей, воронки и сумм сделок он не хранит и не считает. На этом месте стояла
 * карточка лида с двумя репликами; клиент попросил нарисовать звонок-демо, то есть ровно то, что
 * случается ПОСЛЕ ответа.
 *
 * Отсюда главное решение файла: кадр перестал прикидываться экраном продукта. Не карточка лида с
 * дорисованной строчкой про встречу, а отдельная форма подтверждения брони — со своей рамкой, своим
 * героем и нижней строкой, которая прямым текстом называет, где кончилась наша часть. Пока это была
 * «почти карточка лида», каждая новая деталь читалась как ещё одно поле продукта.
 *
 * ЧТО ПОКАЗАНО СВЕРХ ПРОДУКТА — списком, чтобы проверялось построчно:
 *   1. сам факт ответа (без него встречи бы не было);
 *   2. встреча: дата, время, длительность, слово «demo call», статус «подтверждено». Календаря в
 *      продукте нет ни в каком виде;
 *   3. связка «опубликованный ask → созвон» как одна причинно-следственная цепочка. У продукта
 *      здесь существует только первое звено — сигнал в карточке лида.
 * По существу с продукта взяты две вещи: опубликованный ask «rebuilding onboarding» и то, что он
 * лиловый. Остальное на кадре происходит у клиента, а не у нас.
 *
 * СРЕЗАНО ПРИ СБОРКЕ, и это важнее того, что осталось. Первый набросок нёс три цветные панели и
 * четыре уровня подписей: блок «WHY SHE TOOK THE CALL» с кикерами ASKED/REPLIED и цитатой ответа,
 * колонку «ATTENDEES» с двумя аватарами, строку «booked 2 days after the first message» и
 * двухцветный подвал. Он влезал и ничего не обрезал — и всё равно был неправ: смыслов на кадре
 * четыре, а лесов вокруг них было вдвое больше. Осталось по одному носителю на смысл: встреча,
 * человек, причина, граница. Цитата ответа ушла вместе с блоком — чип с опубликованной просьбой
 * говорит то же самое и он настоящий сигнал продукта, а цитата была выдумкой поверх выдумки.
 *
 * ГРАНИЦУ НАШЕЙ ЧАСТИ НЕСЁТ ТЕКСТ ШАГА, А НЕ КАДР. Внутри картинки стояла строка «Intch wrote the
 * first message. The call is yours.» — клиент снял её вместе со всеми синими `proof`-строками
 * блока. Мысль не потеряна: `landing.how.steps.close.text` кончается словами «The call that
 * follows is yours», и это ровно она. ЕСЛИ ЭТОТ ТЕКСТ КОГДА-НИБУДЬ ПЕРЕПИШУТ так, что границы в
 * нём не останется, её придётся вернуть в кадр: бейджа «скоро», как у шага 05, у этого шага нет, и
 * без единого честного слова календарь начнёт читаться как функция продукта.
 *
 * ЛЮДИ И СЛОВА ИЛЛЮСТРАТИВНЫЕ и перенесены из заменяемой версии без изменений: «Maria Kessler»,
 * «Head of Growth», ask «rebuilding onboarding». Их уже проверяли на совпадение с настоящими
 * участниками Intch — см. шапку `StepVisualShortlist.tsx`, где карточка со скриншота принадлежала
 * живому человеку, из-за чего во всех миниатюрах имена и выдуманы. Новое здесь только дата: 10
 * сентября 2026 — действительно четверг, проверено. Подпись «Thursday» рядом с числом обязана
 * сходиться, иначе это первая мелочь, на которой кадр ловят.
 *
 * ЦВЕТА ПО ТАКСОНОМИИ `LeadCard.module.scss`, иначе миниатюра врёт про продукт одними заливками:
 *   --say  — слова САМОГО ЧЕЛОВЕКА, то есть опубликованная просьба. Тот же лиловый, что у чипа
 *            `kind-ask` на настоящей карточке, вместе с его рамкой `--say-line`;
 *   --acc  — встреча. Не `--ok` и не `--gold`: зелёный на настоящей карточке принадлежит НАШЕМУ
 *            выводу, золото — платной двери. Акцент здесь значит «событие»;
 *   --ok   — только статус «подтверждено». Нашего вывода на кадре нет, так что зелёному не с чем
 *            путаться, а подтверждение не зелёным подтверждением не читается;
 *   --gold — ровно одно слово в подвале: первое сообщение. Золото в продукте — цвет платного, и
 *            первое сообщение это единственное платное, что случилось на всём кадре.
 *
 * Служебные подписи — ключи t(). Данные продукта ими не становятся: должности копируются
 * дословно, отрасли и функции — таксономия LinkedIn, тексты разбора и черновика пишет модель,
 * имена и цитаты выдуманы. Всё это остаётся английским и в русской локали — как в продукте. Без зависимостей.* `HowItWorks.tsx`). Текст английский хардкодом.
 */
export const StepVisualDeal = () => {
  const { t } = useTranslation()

  return (
    <div className={styles.root}>
      <div className={styles.status}>
        <i className={styles.tick} aria-hidden="true" />
        {t('landing.how.cards.callConfirmed')}
      </div>

      {/* ---- герой кадра: сама встреча. Плитку даты узнают раньше, чем читают строку времени ---- */}
      <div className={styles.meet}>
        <div className={styles.tile}>
          <span className={styles.tileMonth}>{t('landing.how.cards.monthSep')}</span>
          <span className={styles.tileDay}>10</span>
        </div>
        <div className={styles.when}>
          <div className={styles.time}>16:00 – 16:30</div>
          {/* «demo call» — слово клиента из просьбы, а не наш термин. День недели тут, а не в
              плитке: третья строка в неё не влезает. */}
          <div className={styles.sub}>{t('landing.how.cards.meetingWhen')}</div>
        </div>

        {/* Участник внутри панели, а не отдельной полосой: панель тянется во всю ширину кадра, и
            с одним только временем её правая половина оставалась пустой заливкой. Второго участника
            («You», «Intch») нет намеренно — посадить нас рядом значило бы пообещать, что мы сидим
            на звонке. */}
        <div className={styles.who}>
          <i className={styles.avatar} aria-hidden="true">
            MK
          </i>
          <span className={styles.whoText}>
            <b className={styles.name}>Maria Kessler</b>
            <span className={styles.role}>Head of Growth</span>
          </span>
        </div>
      </div>

      {/* ---- почему созвон случился: её собственная опубликованная просьба, один чип ---- */}
      <div className={styles.why}>
        <span className={styles.whyLabel}>{t('landing.how.cards.shePublished')}</span>
        <span className={styles.chipAsk}>rebuilding onboarding</span>
      </div>
    </div>
  )
}
